import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useUsernameValidation } from '@/hooks/use-username-validation';
import { CheckCircle, XCircle, Loader2, AlertCircle, Lightbulb } from 'lucide-react';
import { cn } from '@/lib/utils';

interface UsernameFieldProps {
  currentUsername: string;
  userId?: string;
  onUsernameChange: (username: string) => Promise<void>;
  isUpdating: boolean;
}

export function UsernameField({ 
  currentUsername, 
  userId, 
  onUsernameChange, 
  isUpdating 
}: UsernameFieldProps) {
  const [inputValue, setInputValue] = useState(currentUsername);
  const [isEditing, setIsEditing] = useState(false);
  const validation = useUsernameValidation(inputValue, userId);

  const handleSave = async () => {
    if (validation.isValid && inputValue !== currentUsername) {
      try {
        await onUsernameChange(inputValue);
        setIsEditing(false);
      } catch (error) {
        console.error('Failed to update username:', error);
      }
    }
  };

  const handleCancel = () => {
    setInputValue(currentUsername);
    setIsEditing(false);
  };

  const getStatusIcon = () => {
    if (validation.isLoading) {
      return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    }
    
    if (!validation.canChange) {
      return <AlertCircle className="h-4 w-4 text-warning" />;
    }
    
    if (validation.isValid) {
      return <CheckCircle className="h-4 w-4 text-success" />;
    }
    
    if (validation.error) {
      return <XCircle className="h-4 w-4 text-destructive" />;
    }
    
    return null;
  };

  const getStatusMessage = () => {
    if (!validation.canChange && validation.nextChangeDate) {
      return `You can change your username again on ${validation.nextChangeDate.toLocaleDateString()}`;
    }
    
    if (validation.error) {
      return validation.error;
    }
    
    if (validation.isValid && inputValue !== currentUsername) {
      return 'Username is available';
    }
    
    return null;
  };

  const useSuggestion = () => {
    if (validation.suggestion) {
      setInputValue(validation.suggestion);
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="username">Username</Label>
      <div className="space-y-2">
        {!isEditing ? (
          <div className="flex items-center space-x-2">
            <Input 
              value={currentUsername} 
              disabled 
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
              disabled={!validation.canChange}
            >
              Edit
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className="relative flex-1">
                <Input
                  id="username"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Enter username"
                  className={cn(
                    "pr-8",
                    validation.isValid && "border-success",
                    validation.error && "border-destructive"
                  )}
                  minLength={3}
                  maxLength={30}
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  {getStatusIcon()}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSave}
                disabled={!validation.isValid || inputValue === currentUsername || isUpdating}
              >
                {isUpdating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Save'
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                disabled={isUpdating}
              >
                Cancel
              </Button>
            </div>
            
            {getStatusMessage() && (
              <p className={cn(
                "text-sm",
                validation.isValid ? "text-success" : "text-destructive",
                !validation.canChange && "text-warning"
              )}>
                {getStatusMessage()}
              </p>
            )}
            
            {validation.suggestion && !validation.isValid && validation.canChange && (
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Lightbulb className="h-4 w-4" />
                <span>Suggestion:</span>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-primary"
                  onClick={useSuggestion}
                >
                  {validation.suggestion}
                </Button>
              </div>
            )}
            
            <p className="text-xs text-muted-foreground">
              Username must be 3-30 characters and can only contain letters, numbers, and underscores.
              You can change your username once every 30 days.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}